/* Sus Reels. Una sola regla: NADA con voz arranca solo y el video pesado no se
   descarga hasta que la persona lo pide. El `preload="none"` del HTML es la
   mitad; esta es la otra.

   Suenan CON audio, no mudos: son videos donde ella habla, y quitarle la voz a
   un video de alguien hablando es quedarse con la mitad. El navegador lo
   permite porque el play sale de un clic — de un gesto real, no de un guion. */
(() => {
  const marcos = [...document.querySelectorAll('.reel .marco, .voz-clip')]
    .filter(m => m.querySelector('video'));
  if (!marcos.length) return;

  const quieto = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parar = (m) => {
    const v = m.querySelector('video');
    v.pause(); m.classList.remove('sonando');
  };

  marcos.forEach(m => {
    const v = m.querySelector('video');
    const boton = m.querySelector('.play');

    const alterna = () => {
      if (!v.paused) return parar(m);
      // Uno a la vez: dos videos hablando encima del otro no se entiende ninguno.
      marcos.forEach(o => o !== m && parar(o));
      v.play().then(() => m.classList.add('sonando'))
              .catch(() => { v.muted = true; v.play(); m.classList.add('sonando') });
    };

    boton?.addEventListener('click', alterna);
    v.addEventListener('click', alterna);
    v.addEventListener('ended', () => m.classList.remove('sonando'));
  });

  // Si el video se va de la pantalla, se calla: seguir oyéndola tres secciones
  // más abajo es de las cosas que hacen cerrar una pestaña.
  const ojo = new IntersectionObserver(es => es.forEach(e => {
    if (!e.isIntersecting) parar(e.target);
  }), { threshold: 0 });
  marcos.forEach(m => ojo.observe(m));

  /* ── QoL · avance, silencio y Escape ──────────────────────────────────────
     Tres huecos que se notan justo cuando el video YA está sonando, que es el
     momento en que la persona está más metida y menos dispuesta a perdonar:
     no saber cuánto falta (y uno de los Reels dura 55 s contra 16 de los
     otros), no poder callarlo sin perderlo, y no tener salida de teclado. */
  const ico = (id) =>
    `<svg class="ico" aria-hidden="true"><use href="#${id}"/></svg>`;

  marcos.filter(m => m.closest('.reel')).forEach(m => {
    const v = m.querySelector('video');

    const barra = document.createElement('span');
    barra.className = 'avance'; barra.setAttribute('aria-hidden', 'true');
    barra.innerHTML = '<i></i>';
    const relleno = barra.firstChild;
    m.appendChild(barra);

    // `scaleX` sobre una variable: el avance vive en el compositor. Escribir
    // `width` 24 veces por segundo reflota el layout de la fila entera.
    v.addEventListener('timeupdate', () => {
      if (!v.duration) return;
      relleno.style.setProperty('--av', (v.currentTime / v.duration).toFixed(4));
    });

    const mudo = document.createElement('button');
    mudo.className = 'mudo'; mudo.type = 'button';
    mudo.innerHTML = ico('i-sonido');
    mudo.setAttribute('aria-label', 'Silenciar el video');
    mudo.addEventListener('click', (e) => {
      e.stopPropagation();               // si no, el clic llega al marco y pausa
      v.muted = !v.muted;
      mudo.innerHTML = ico(v.muted ? 'i-mudo' : 'i-sonido');
      mudo.setAttribute('aria-label', v.muted ? 'Activar el sonido' : 'Silenciar el video');
    });
    m.appendChild(mudo);
  });

  addEventListener('keydown', (e) => {
    if (e.key === 'Escape') marcos.forEach(parar);
  });

  /* ── LA OJEADA: el bucle mudo, y lo manda el puntero ──────────────────────
     Un bucle de 4 s por marco, sin voz. La voz sigue detrás del clic.

     🔴 EMPEZÓ CORRIENDO SOLO Y SE CAMBIÓ. Los cuatro arrancaban al entrar a
     cuadro (el patrón de Tolstoy) y paco lo paró con razón: cuatro videos
     distintos moviéndose a la vez no son un muro vivo, son cuatro cosas
     peleándose. Con cuatro focos de movimiento simultáneos no hay dónde mirar,
     que es exactamente el problema contrario al que se quería resolver.

     Ahora el movimiento lo pide la persona:
       · con ratón — corre el marco que estás señalando, y solo ese;
       · con teclado — igual, al recibir el foco (es el hover de quien no usa
         ratón; sin esto la mejora existe solo para una parte de la gente);
       · con dedo — no hay hover, así que corre el que quedó centrado en la
         fila que arrastra: uno a la vez, que es lo que ya se había medido.
     El puntero se detecta por capacidad (`hover:hover`), no por ancho de
     pantalla: una laptop táctil y un iPad quedan del lado correcto.

     Se construye desde JS a propósito: así, cuando NO toca cargarlo, no existe
     ni el elemento. Los tres casos en que no toca:
       · movimiento reducido — es una preferencia del sistema, no un detalle;
       · ahorro de datos activo — son 653 KB que nadie pidió;
       · sin JS — se queda el póster, que es lo que había antes.               */
  const ahorro = navigator.connection && navigator.connection.saveData;
  if (quieto || ahorro) return;

  const conOjeada = marcos.filter(m => m.dataset.ojeada);
  if (!conOjeada.length) return;

  conOjeada.forEach(m => {
    const o = document.createElement('video');
    o.className = 'ojeada';
    o.muted = true; o.loop = true; o.playsInline = true; o.preload = 'none';
    o.setAttribute('aria-hidden', 'true');   // es decoración: el contenido es el clip
    o.tabIndex = -1;
    // Después del clip para que los botones (hermanos posteriores) queden encima.
    m.querySelector('.clip').after(o);
    m._oj = o;
  });

  // Hay puntero de verdad (ratón/trackpad). Se consulta en cada evento y no una
  // sola vez: un iPad con teclado acoplado cambia de modo en caliente.
  const conPuntero = () => matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* Si UNO tiene la voz, ningún bucle corre. Se pregunta por el ESTADO DEL
     VIDEO, no por la clase: la clase se pone dentro del `.then()` de `play()`,
     que resuelve DESPUÉS del evento `play`. Preguntando por la clase, el
     primer reparto siempre llegaba antes de que existiera. */
  const hablaAlguien = () => marcos.some(m => {
    const v = m.querySelector('.clip') || m.querySelector('video');
    return v && !v.paused;
  });

  const enciende = (m) => {
    if (hablaAlguien()) return;
    const o = m._oj;
    if (!o.src) o.src = m.dataset.ojeada;   // no se pide un byte hasta aquí
    o.currentTime = 0;                      // cada ojeada empieza desde el principio
    m.classList.add('ojeando');
    // `play()` devuelve promesa: sin el catch, una pausa a medio arranque
    // ensucia la consola con un AbortError que no significa nada.
    o.play().catch(() => {});
  };
  const apaga = (m) => {
    m.classList.remove('ojeando');
    if (m._oj.src) m._oj.pause();
  };
  const apagaTodos = () => conOjeada.forEach(apaga);

  // ── con ratón o con teclado: corre el que señalas, y solo ese ──
  conOjeada.forEach(m => {
    const entra = () => { if (conPuntero()) { apagaTodos(); enciende(m) } };
    const sale  = () => { if (conPuntero()) apaga(m) };
    m.addEventListener('pointerenter', entra);
    m.addEventListener('pointerleave', sale);
    // El foco del teclado es el hover de quien no usa ratón. Va en el marco
    // porque el que recibe foco es el botón de play, que vive dentro.
    m.addEventListener('focusin', entra);
    m.addEventListener('focusout', sale);
  });

  // ── con el dedo: el que quedó centrado en la fila que arrastra ──
  const razon = new Map();
  const repartir = () => {
    if (conPuntero()) return;               // con ratón manda el puntero, no esto
    let mejor = null, max = 0;
    razon.forEach((r, m) => { if (r > max) { max = r; mejor = m } });
    const calla = hablaAlguien();
    conOjeada.forEach(m => {
      (!calla && m === mejor && max > .5) ? enciende(m) : apaga(m);
    });
  };

  const ojoOjeada = new IntersectionObserver(es => {
    es.forEach(e => razon.set(e.target, e.intersectionRatio));
    repartir();
  }, { threshold: [0, .25, .5, .55, .75, 1] });
  conOjeada.forEach(m => ojoOjeada.observe(m));

  // Al pedir la voz, todo bucle se apaga. Y al pausar vuelve el de abajo del
  // ratón — si no, el marco con el que la persona acaba de interactuar se queda
  // en póster muerto justo mientras lo sigue señalando.
  conOjeada.forEach(m => {
    const v = m.querySelector('.clip');
    v.addEventListener('play', apagaTodos);
    ['pause', 'ended'].forEach(ev => v.addEventListener(ev, () => {
      if (!conPuntero()) return repartir();
      conOjeada.forEach(x => { if (x.matches(':hover')) enciende(x) });
    }));
  });

  // En celular la fila arrastra: al soltar, el que quedó centrado toma el turno.
  document.querySelector('.reels .fila')
    ?.addEventListener('scroll', () => {
      clearTimeout(repartir._t);
      repartir._t = setTimeout(repartir, 120);
    }, { passive: true });

  addEventListener('resize', repartir, { passive: true });
})();

/* ── EL DEDO: cuántos hay y en cuál vas ─────────────────────────────────────
   La fila de Reels arrastra en celular y la única pista era que se asomaba un
   pedazo del siguiente — que dice "hay más", pero no cuántos ni dónde estás.
   Cuatro rayas lo resuelven. Se construyen desde JS para que su número salga
   de los Reels que de verdad existan y no de un número escrito a mano que se
   desincroniza el día que se agregue o se quite uno.                          */
(() => {
  const fila = document.querySelector('.reels .fila');
  if (!fila) return;
  const reels = [...fila.querySelectorAll('.reel')];
  if (reels.length < 2) return;

  const puntos = document.createElement('div');
  puntos.className = 'puntos';
  puntos.setAttribute('aria-hidden', 'true');   // el orden ya lo da la lista
  reels.forEach(() => puntos.appendChild(document.createElement('i')));
  fila.after(puntos);
  const rayas = [...puntos.children];

  let pendiente = false;
  const marcar = () => {
    pendiente = false;
    const c = fila.scrollLeft + fila.clientWidth / 2;
    let cual = 0, cerca = Infinity;
    reels.forEach((r, i) => {
      const d = Math.abs((r.offsetLeft + r.offsetWidth / 2) - c);
      if (d < cerca) { cerca = d; cual = i }
    });
    rayas.forEach((x, i) => x.classList.toggle('aqui', i === cual));
  };
  fila.addEventListener('scroll', () => {
    if (!pendiente) { pendiente = true; requestAnimationFrame(marcar) }
  }, { passive: true });
  addEventListener('resize', marcar, { passive: true });
  marcar();
})();
